/**
 * Event-role helpers.
 *
 * Today the app distinguishes between owner / admin (full access) and
 * "attendee" (any other authenticated viewer). The DB schema already
 * supports arbitrary role strings on `event_roles.role` though, so this
 * helper is designed to grow: add a new role to the array below and the
 * downstream components pick it up without code changes.
 *
 * Roles map to capability sets, not 1:1 to UI surfaces. New surfaces
 * should check the capability (e.g. `canEdit(roles)`), not the role
 * string directly, so re-keying capabilities later is a one-file change.
 */

/** Roles that get the dashboard admin shell (with Edit + admin tabs). */
export const ADMIN_SHELL_ROLES = ['owner', 'admin', 'manager'] as const

/** Roles that can edit event metadata. */
export const EDIT_ROLES = ['owner', 'admin', 'editor'] as const

/** Roles that can view the attendees list, form responses, etc. */
export const VIEW_ATTENDEE_DATA_ROLES = ['owner', 'admin', 'manager'] as const

/** Roles that can post announcements. */
export const POST_ANNOUNCEMENT_ROLES = ['owner', 'admin'] as const

/** Roles that can see aggregate authenticated announcement reads. */
export const VIEW_ANNOUNCEMENT_METRICS_ROLES = ['owner', 'admin'] as const

/** Roles that can delete the event. */
export const DELETE_EVENT_ROLES = ['owner'] as const

/**
 * Task 98 · who may actually send (`ramme 8c`, «Ingen tilgang til å sende»).
 *
 * The frame is explicit that the capability comes from THIS file «ikke en ny
 * rolle» — so it is a named capability over roles that already exist, and
 * adding a role stays a one-file change.
 *
 * These are ORGANISATION roles, not event roles: a broadcast belongs to a
 * tenant, and «may post an announcement on one event» is a smaller power than
 * «may text nine hundred people». Keeping them separate costs one constant and
 * means widening one never silently widens the other.
 */
export const ORG_SEND_ROLES = ['owner', 'admin'] as const

/** May this organisation role send a broadcast? */
export const canSendBroadcast = (orgRole: string | null | undefined) =>
  !!orgRole && (ORG_SEND_ROLES as readonly string[]).includes(orgRole)

export function hasAnyRole(
  userRoles: readonly string[] | null | undefined,
  allowed: readonly string[]
): boolean {
  if (!userRoles?.length) return false
  return userRoles.some((r) => allowed.includes(r))
}

export const canViewAdminShell = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, ADMIN_SHELL_ROLES)

export const canEdit = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, EDIT_ROLES)

export const canViewAttendeeData = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, VIEW_ATTENDEE_DATA_ROLES)

export const canPostAnnouncements = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, POST_ANNOUNCEMENT_ROLES)

export const canViewAnnouncementMetrics = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, VIEW_ANNOUNCEMENT_METRICS_ROLES)

export const canDeleteEvent = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, DELETE_EVENT_ROLES)

export const isOwner = (roles: readonly string[] | null | undefined) =>
  !!roles?.includes('owner')

// ── Account type — what a person can CREATE ────────────────────────────────
//
// `profiles.account_type` is chosen by the user in settings («Arrangør» /
// «Leverandør» / «Begge»), so it is a direct statement of intent. The bottom
// nav used `isVenueOrOrganizer` instead — a boolean derived from
// `profiles.role` — which cannot express "both" at all, and that is precisely
// the ambiguity round 22b objects to: one button, one word, two different
// objects created depending on a flag the user never set.

export type CreatableObject = 'event' | 'offer'

/**
 * What this account may create, in the order the UI should offer it.
 *
 * The account type is a preference; the organisation's modules decide what exists. A vendor
 * preference in an organisation with Offers switched off used to make «Nytt tilbud» the mobile
 * primary action, which opened the disabled-module page (F2, 21.10.2026). Preference now chooses
 * among the AVAILABLE objects, and an event is the fallback when it leaves nothing.
 */
export function creatableObjects(
  accountType: string | null | undefined,
  modules: { offersEnabled?: boolean } = {}
): readonly CreatableObject[] {
  const preferred: readonly CreatableObject[] =
    accountType === 'vendor' ? ['offer']
      : accountType === 'both' ? ['event', 'offer']
        // 'organiser' and anything unset — the historical default.
        : ['event']
  if (modules.offersEnabled !== false) return preferred
  const available = preferred.filter((o) => o !== 'offer')
  return available.length > 0 ? available : ['event']
}
